import { z } from "zod";
import { ConversionMapping, PayoutModel, StatusWord } from "@gen/tracker/v1/affiliate_network_pb";
import type { ConversionStatus } from "@gen/tracker/v1/conversion_pb";
import { ActionConfig, ActionType, CapsConfig, Offer, OfferStatus, PayoutConfig, PayoutRule } from "@gen/tracker/v1/offer_pb";
import { type FieldError, Money } from "@gen/tracker/v1/common_pb";

/**
 * The offer editor's form (boards OfferEditor, OfferEditorErrors): every
 * field a structure — countries a list, payouts per country rows, parameters
 * key/value rows, the status override word → status rows. No text format and
 * no JSON anywhere: the one thing a person types is a value in its own box.
 * The service is authoritative (ValidateOffer); `placeError` puts each of
 * its problems under the box that holds it. Pure.
 */

// --- the form ----------------------------------------------------------------

const row = z.object({ id: z.string() });

/**
 * The editor's values. Money is a string as typed («32,00» or «32.00»);
 * counts are strings so an empty box is «без лимита», not 0 typed by hand.
 */
export const offerFormSchema = (t: (key: "u6.err.nameRequired" | "u6.err.networkRequired") => string) =>
  z.object({
    name: z.string().trim().min(1, t("u6.err.nameRequired")),
    networkId: z.string().min(1, t("u6.err.networkRequired")),
    countries: z.array(z.string()),
    status: z.enum(["active", "paused"]),
    url: z.string(),
    redirect: z.boolean(),
    payoutModel: z.string(),
    payoutAmount: z.string(),
    rules: z.array(row.extend({ country: z.string(), model: z.string(), amount: z.string() })),
    dailyCap: z.string(),
    totalCap: z.string(),
    capTimezone: z.string(),
    params: z.array(row.extend({ key: z.string(), value: z.string() })),
    goalPreset: z.string(),
    statuses: z.array(row.extend({ word: z.string(), status: z.string() })),
  });

export type OfferFormValues = z.infer<ReturnType<typeof offerFormSchema>>;

let seq = 0;
/** A stable key for a new row (DynamicList keys rows by it, never by index). */
export function rowId(): string {
  seq += 1;
  return `r${seq}`;
}

/** «32.000000» as a person types it: «32,00». Empty for nothing or zero. */
const fromDecimal = (s: string | undefined) => {
  const n = Number(s);
  return s && Number.isFinite(n) && n !== 0 ? n.toFixed(Math.max(2, (String(n).split(".")[1] ?? "").length)).replace(".", ",") : "";
};
const toDecimal = (s: string) => s.trim().replace(/\s/g, "").replace(",", ".");
const capText = (n: number | undefined) => (n && n > 0 ? String(n) : "");
const capNumber = (s: string) => {
  const n = Number(s.trim());
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
};

/** The editor's values for an offer — a blank draft when there is none. */
export function offerToForm(o: Offer | undefined, defaults: { networkId?: string; timezone: string }): OfferFormValues {
  if (!o) {
    return {
      name: "",
      networkId: defaults.networkId ?? "",
      countries: [],
      status: "active",
      url: "",
      redirect: true,
      payoutModel: String(PayoutModel.CPA),
      payoutAmount: "",
      rules: [],
      dailyCap: "",
      totalCap: "",
      capTimezone: defaults.timezone,
      params: [],
      goalPreset: "",
      statuses: [],
    };
  }
  return {
    name: o.name,
    networkId: o.networkId,
    countries: [...o.country],
    status: o.status === OfferStatus.PAUSED ? "paused" : "active",
    url: o.action?.payload ?? "",
    redirect: o.action?.type !== ActionType.LINK,
    payoutModel: String(o.payout?.model || PayoutModel.CPA),
    payoutAmount: fromDecimal(o.payout?.amount?.amount),
    rules: (o.payout?.rules ?? []).map((r) => ({
      id: rowId(),
      country: r.country,
      model: String(r.model || o.payout?.model || PayoutModel.CPA),
      amount: fromDecimal(r.amount?.amount),
    })),
    dailyCap: capText(o.caps?.dailyConversions),
    totalCap: capText(o.caps?.totalConversions),
    capTimezone: o.caps?.timezone || defaults.timezone,
    params: Object.entries(o.customParams)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => ({ id: rowId(), key, value })),
    goalPreset: o.goalPreset,
    statuses: (o.conversionMapping?.statuses ?? []).map((s) => ({ id: rowId(), word: s.word, status: String(s.status) })),
  };
}

/**
 * The offer to send. `base` is the offer as read: what the editor does not
 * show (click caps, external ids, notes, the fallback offer, the mapping's
 * goal words) rides along unchanged. Currency is the partner's.
 */
export function formToOffer(v: OfferFormValues, base: Offer | undefined, currency: string): Offer {
  const o = base ? base.clone() : new Offer();
  const model = Number(v.payoutModel) as PayoutModel;
  o.name = v.name.trim();
  o.networkId = v.networkId;
  o.country = v.countries;
  o.status = v.status === "paused" ? OfferStatus.PAUSED : base?.status === OfferStatus.CAPPED ? OfferStatus.CAPPED : OfferStatus.ACTIVE;
  o.action = new ActionConfig({
    type: v.redirect ? ActionType.REDIRECT : ActionType.LINK,
    payload: v.url.trim(),
    options: base?.action?.options ?? {},
  });
  o.payout = new PayoutConfig({
    model,
    amount: new Money({ amount: toDecimal(v.payoutAmount), currency }),
    rules: v.rules.map(
      (r) =>
        new PayoutRule({
          country: r.country,
          model: Number(r.model) as PayoutModel,
          amount: new Money({ amount: toDecimal(r.amount), currency }),
        }),
    ),
  });
  o.caps = new CapsConfig({
    ...(base?.caps ?? {}),
    dailyConversions: capNumber(v.dailyCap),
    totalConversions: capNumber(v.totalCap),
    timezone: v.capTimezone,
  });
  o.customParams = Object.fromEntries(v.params.filter((p) => p.key.trim() || p.value.trim()).map((p) => [p.key.trim(), p.value]));
  o.goalPreset = v.goalPreset;
  const mapping = base?.conversionMapping?.clone() ?? new ConversionMapping();
  mapping.statuses = v.statuses
    .filter((s) => s.word.trim() || s.status)
    .map((s) => new StatusWord({ word: s.word.trim(), status: Number(s.status || 0) as ConversionStatus }));
  o.conversionMapping = mapping;
  return o;
}

// --- server problems onto fields ------------------------------------------------

/** The editor's sections, in order (board OfferEditor 1–6). */
export const SECTIONS = ["main", "link", "payout", "caps", "params", "conversions"] as const;
export type Section = (typeof SECTIONS)[number];

export interface PlacedError {
  /** The form field the message lands under; "root" when no field holds it. */
  field: string;
  section: Section;
  message: string;
  code: string;
}

/**
 * Where a ValidateOffer problem belongs: `payout.rules[2].country` is the
 * third rule's country box in «Выплата»; `custom_params[lang]` is the row
 * whose key is lang. A path the form does not draw lands on its section. Pure.
 */
export function placeError(e: FieldError, v: OfferFormValues): PlacedError {
  const p = e.path;
  const at = (field: string, section: Section): PlacedError => ({ field, section, message: e.message, code: e.code });
  let m: RegExpMatchArray | null;
  if (p === "name") return at("name", "main");
  if (p === "network_id") return at("networkId", "main");
  if (p === "status") return at("status", "main");
  if (p === "country" || p.startsWith("country[")) return at("countries", "main");
  if (p.startsWith("action")) return at("url", "link");
  if ((m = p.match(/^payout\.rules\[(\d+)\]\.(country|amount|model)/))) return at(`rules.${m[1]}.${m[2]}`, "payout");
  if (p.startsWith("payout")) return at(p === "payout.model" ? "payoutModel" : "payoutAmount", "payout");
  if (p === "caps.daily_conversions") return at("dailyCap", "caps");
  if (p === "caps.total_conversions") return at("totalCap", "caps");
  if (p === "caps.timezone") return at("capTimezone", "caps");
  if (p.startsWith("caps")) return at("root", "caps");
  if ((m = p.match(/^custom_params\[(.*)\]$/))) {
    const i = v.params.findIndex((r) => r.key.trim() === m![1]);
    return at(i >= 0 ? `params.${i}.key` : "root", "params");
  }
  if (p === "custom_params") return at("root", "params");
  if (p === "goal_preset") return at("goalPreset", "conversions");
  if ((m = p.match(/^conversion_mapping\.statuses\[(\d+)\]\.(word|status)/))) return at(`statuses.${m[1]}.${m[2]}`, "conversions");
  if (p.startsWith("conversion_mapping")) return at("root", "conversions");
  return at("root", "main");
}

/**
 * The form's own problems (no name, no partner — the schema above) as the
 * editor's summary reads them: each under its field, in its section. Pure.
 */
export function formProblems(errors: Partial<Record<string, unknown>>): PlacedError[] {
  const out: PlacedError[] = [];
  for (const [field, section] of [
    ["name", "main"],
    ["networkId", "main"],
  ] as const) {
    const e = errors[field] as { message?: unknown } | undefined;
    if (e && typeof e.message === "string" && e.message) out.push({ field, section, message: e.message, code: "FORM" });
  }
  return out;
}

/** The service's problems first; a form problem only on a field the service left alone. Pure. */
export function mergeProblems(server: PlacedError[], form: PlacedError[]): PlacedError[] {
  const named = new Set(server.map((p) => p.field));
  return [...server, ...form.filter((p) => !named.has(p.field))];
}

/** The fields an offer source owns (UpdateOffer refuses them as SOURCE_OWNED). */
export function sourceLocked(o: Offer | undefined): boolean {
  return !!o?.source;
}
